import { useEffect, useRef, useState } from 'react'
import { useCached } from '../lib/cache'
import { loadEmployees, idSignIn, clockIn } from '../lib/api'
import { supabase } from '../lib/supabase'
import { IdPad } from '../components/IdPad'
import { I } from '../components/Icons'
import { Working } from '../components/StarTick'
import { StarPulse } from '../components/Stardust'
import { shake } from '../lib/motion'
import { openDay, nyToday, type DayState } from '../state/day'
import type { Employee } from '../lib/types'

const time = (iso: string) => new Date(iso).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: 'America/New_York' })

/** Sign in with your ID. On a device with no session yet the ID is checked by the server, which signs the device in
 *  (no pairing screen). Then, if you are not on the clock today, you clock in before the app opens. */
export function Open({ onOpen, signedIn }: { onOpen: (d: DayState) => void; signedIn: boolean }) {
  const [who, setWho] = useState<Employee | null>(null)
  const [clock, setClock] = useState<'checking' | 'off' | null>(null)
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: 'America/New_York' })

  const enter = async (e: Employee) => { setBusy(true); onOpen(await openDay(e)); setBusy(false) }
  // after the ID: on the clock today → straight in; otherwise the clock-in step
  const identified = async (e: Employee) => {
    setWho(e); setErr(''); setClock('checking')
    const { data, error } = await supabase.from('time_punches').select('id').eq('employee_id', e.employee_id).eq('day', nyToday()).is('clock_out', null).maybeSingle()
    if (error) { setErr('Can\'t reach the server. Check the wifi.'); setClock('off'); return }
    if (data) { await enter(e); return }
    setClock('off')
  }
  const doClockIn = async () => {
    if (!who) return
    setBusy(true); setErr('')
    try { await clockIn(who.employee_id); onOpen(await openDay(who)) } catch (ex) { setErr((ex as Error).message) } finally { setBusy(false) }
  }

  // Motion: the moment the ID is accepted the box gives way to the black screen with the pulsing star while the clock state
  // is checked (no "One moment" box); if the person is on the clock the welcome takes over from that same star, otherwise the
  // Clock in box comes back.
  if (who && clock === 'checking') return <StarPulse />
  if (who && clock) {
    return (
      <div className="open">
        <div className="open__box">
          <img src="/brand/wordmark-black.png" alt="Starchive" />
          <div className="sheet__title">Hi {who.name}</div>
          <div className="hint" style={{ textAlign: 'center', paddingTop: 2 }}>You're not on the clock yet. Clock in to start your shift.</div>
            <div className="clockin__time">{time(new Date().toISOString())}</div>
            {err && <div className="sheet__sub bad">{err}</div>}
            <button type="button" className="cta cta--center sheet__cta" disabled={busy} onClick={doClockIn}>{busy ? <Working>Clocking in</Working> : 'Clock in'}</button>
            <button type="button" className="btn-secondary" style={{ width: '100%', marginTop: 10 }} disabled={busy} onClick={() => { setWho(null); setClock(null) }}>Not {who.name}? Back</button>
        </div>
      </div>
    )
  }
  return (
    <div className="open">
      <div className="open__box">
        <img src="/brand/wordmark-black.png" alt="Starchive" />
        <div className="sheet__title">Sign in with your ID</div>
        <div className="hint" style={{ textAlign: 'center', paddingTop: 2 }}>{today}</div>
        {signedIn ? <LocalPad onConfirm={identified} busy={busy} /> : <ServerPad onConfirm={identified} />}
        <div className="hint" style={{ textAlign: 'center', paddingTop: 14 }}>Clock in when you arrive and out when you leave. Everything you do is signed with this ID; the app locks itself after 5 minutes idle.</div>
      </div>
    </div>
  )
}

function LocalPad({ onConfirm, busy }: { onConfirm: (e: Employee) => void; busy: boolean }) {
  // the employee list from the read cache: the pad works the moment the lock screen appears; the list is re-read behind it
  const E = useCached<Employee[]>('employees', loadEmployees, { staleMs: 0 })
  const employees = E.data ?? []
  if (!E.data && E.error) return <div className="sheet__sub bad">Can't reach the server. Check the wifi.</div>
  return <IdPad employees={employees} onConfirm={onConfirm} busy={busy} confirmLabel="Sign in" busyLabel="Signing in" />
}

/** The same pad for a device with no session: the ID is checked by the server (wrong IDs count towards a lockout). */
function ServerPad({ onConfirm }: { onConfirm: (e: Employee) => void }) {
  const [id, setId] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState('')
  const boxes = useRef<HTMLDivElement>(null)
  const go = async () => {
    if (id.length !== 4 || busy) return
    setBusy(true); setMsg('')
    try { const e = await idSignIn(id); onConfirm(e) } catch (ex) { setMsg((ex as Error).message); shake(boxes.current); window.setTimeout(() => setId(''), 420) } finally { setBusy(false) }
  }
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (/^[0-9]$/.test(e.key)) { setMsg(''); setId((v) => (v + e.key).slice(0, 4)) }
      else if (e.key === 'Backspace') setId((v) => v.slice(0, -1))
      else if (e.key === 'Enter') go()
    }
    window.addEventListener('keydown', onKey); return () => window.removeEventListener('keydown', onKey)
  })
  const key = (k: string) => { setMsg(''); setId((v) => (v + k).slice(0, 4)) }
  return (
    <div className="idpad">
      <div className={'sheet__sub' + (msg ? ' bad' : '')}>{msg || 'Enter your four-digit employee ID'}</div>
      <div className="idboxes" ref={boxes}>{[0, 1, 2, 3].map((i) => <div key={i} className={'idbox' + (i === id.length ? ' cur' : '')}>{i < id.length ? <span className="m-pop">•</span> : ''}</div>)}</div>
      <div className="pad">
        {['1', '2', '3', '4', '5', '6', '7', '8', '9'].map((k) => <button key={k} type="button" className="pad__key" onClick={() => key(k)}>{k}</button>)}
        <button type="button" className="pad__key ghost" onClick={() => setId('')}>Clear</button>
        <button type="button" className="pad__key" onClick={() => key('0')}>0</button>
        <button type="button" className="pad__key ghost" aria-label="Delete" onClick={() => setId((v) => v.slice(0, -1))}>{I.del()}</button>
      </div>
      <button type="button" className="cta cta--center sheet__cta" disabled={id.length !== 4 || busy} onClick={go}>{busy ? <Working>Signing in</Working> : 'Sign in'}</button>
    </div>
  )
}
